import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { WorkOrdersOverviewService } from '../../services/work-orders-overview.service';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import {
  AccountFilterData,
  DateIntervalFilterData,
  WidgetFilterData,
} from '../../../../../_shared/models';
import {
  CalendarConfig,
  CalendarEvent,
  CalendarTodayEvent,
  CalendarFilterOption,
  CalendarDay,
} from '../../../../_shared/models/calendar.model';
import { DateInterval } from '../../../../../_shared/enums';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { SharedCalendarComponent } from '../../../../_shared/components/calendar/calendar.component';
import { WorkshopEventScheduleModalComponent } from '../modals/event-schedule-modal/event-schedule-modal.component';
import {
  TodayWorkshopEventResponse,
  TodayWorkshopEventsPaginationRequest,
  WorkshopEventScheduleData,
  WorkshopEventScheduleResponse,
  WorkshopEventResponse,
  WorkshopEventsByDateRequest,
  WorkshopEventsPreviewRequest,
} from '../../models/work-order.models';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-workshop-calendar-overview',
  standalone: true,
  imports: [CommonModule, SharedCalendarComponent, AccountFilterComponent],
  templateUrl: './workshop-calendar-overview.component.html',
  styleUrl: './workshop-calendar-overview.component.scss',
})
export class WorkshopCalendarOverviewComponent implements OnInit, OnDestroy {
  private filterService = inject(FilterService);
  private workOrdersService = inject(WorkOrdersOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  // Filter state
  accountFilter: AccountFilterData = {} as AccountFilterData;
  dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;

  // Calendar configuration
  calendarConfig: CalendarConfig = {
    title: this.transloco.translate('workOrder.calendar.title'),
    backLink: '/asset-management/work-order',
    searchPlaceholder: this.transloco.translate('workOrder.calendar.searchPlaceholder'),
    filterSectionTitle: this.transloco.translate('workOrder.calendar.workType'),
    filterOptions: [
      { key: 'major', label: this.transloco.translate('workOrder.serviceType.majorService'), color: '#F5A623', checked: true },
      {
        key: 'intermediate',
        label: this.transloco.translate('workOrder.serviceType.intermediateService'),
        color: '#F4BB2A',
        checked: true,
      },
      { key: 'minor', label: this.transloco.translate('workOrder.serviceType.minorService'), color: '#3873C9', checked: true },
    ],
    missedEventFilterSectionTitle: this.transloco.translate('workOrder.calendar.workStatus'),
    missedEventFilter: {
      key: 'missed',
      label: this.transloco.translate('workOrder.calendar.missedService'),
      color: '#9FA1A4',
      checked: true,
    },
    todaySectionTitle: this.transloco.translate('workOrder.calendar.today'),
    showTodaySection: true,
  };

  // Date filter config
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  // Data
  isLoadingAllEvents: boolean = true;
  isLoadingTodayEvents: boolean = true;
  expandedDayLoadingKey: string | null = null;
  top4Events: CalendarEvent[] = [];
  expandedDayEvents: CalendarEvent[] = [];
  expandedDayKey: string | null = null;
  todayEvents: CalendarTodayEvent[] = [];
  todayPage: number = 1;
  todayTotalPages: number = 0;
  isEventScheduleModalOpen: boolean = false;

  // Internal state
  private searchTerm: string = '';
  private currentDate: Date = new Date();
  private cancelSubject: Subject<void> = new Subject<void>();
  private isUpdatingDraggedEvent: boolean = false;

  ngOnInit(): void {
    this.transloco.selectTranslation().pipe(takeUntil(this.cancelSubject)).subscribe(() => {
      this.refreshCalendarLabels();
    });
  }

  private refreshCalendarLabels(): void {
    const translate = (key: string) => this.transloco.translate(key);
    const labels: Record<string, string> = {
      minor: translate('workOrder.serviceType.minorService'),
      intermediate: translate('workOrder.serviceType.intermediateService'),
      major: translate('workOrder.serviceType.majorService'),
    };
    this.calendarConfig = {
      ...this.calendarConfig,
      title: translate('workOrder.calendar.title'),
      searchPlaceholder: translate('workOrder.calendar.searchPlaceholder'),
      filterSectionTitle: translate('workOrder.calendar.workType'),
      filterOptions: this.calendarConfig.filterOptions.map(option => ({ ...option, label: labels[option.key] ?? option.label })),
      missedEventFilterSectionTitle: translate('workOrder.calendar.workStatus'),
      missedEventFilter: this.calendarConfig.missedEventFilter ? {
        ...this.calendarConfig.missedEventFilter,
        label: translate('workOrder.calendar.missedService'),
      } : undefined,
      todaySectionTitle: translate('workOrder.calendar.today'),
    };
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  private updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter,
      } as WidgetFilterData);

      this.resetMonthCalendarEventsState();
      this.resetTodayCalendarEventsState();
    }
  }

  onFilterChange(options: CalendarFilterOption[]): void {
    this.calendarConfig.filterOptions = options;
    this.clearExpandedDayEvents();
    void this.loadCalendarEventsForCurrentView();
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.clearExpandedDayEvents();
    void this.loadCalendarEventsForCurrentView();
  }

  onDateChange(dates: { currentDate: Date; selectedDate: Date }): void {
    this.currentDate = dates.currentDate;
    const pivot = dates.selectedDate ?? dates.currentDate;

    switch (this.dateFilter.interval) {
      case DateInterval.Day:
        this.dateFilter.startDate = startOfDay(pivot);
        this.dateFilter.endDate = endOfDay(pivot);
        break;
      case DateInterval.Week:
        this.dateFilter.startDate = startOfWeek(pivot, { weekStartsOn: 0 });
        this.dateFilter.endDate = endOfWeek(pivot, { weekStartsOn: 0 });
        break;
      case DateInterval.Month:
      default:
        this.dateFilter.startDate = startOfMonth(this.currentDate);
        this.dateFilter.endDate = endOfMonth(this.currentDate);
        break;
    }

    void this.resetMonthCalendarEventsState();
  }

  onTodayScrollDown(): void {
    if (this.isLoadingTodayEvents || this.todayPage >= this.todayTotalPages)
      return;

    this.todayPage++;
    void this.loadTodayCalendarEventsData();
  }

  onMonthDayExpand(day: CalendarDay): void {
    const dayKey = this.getDayKey(day.date);

    if (this.expandedDayLoadingKey === dayKey) {
      return;
    }

    void this.loadCalendarEventsByDateData(day.date);
  }

  async onEventClick(event: CalendarEvent): Promise<void> {
    if (this.isEventScheduleModalOpen) {
      return;
    }

    this.isEventScheduleModalOpen = true;

    try {
      const response =
        await this.workOrdersService.getWorkshopEventScheduleByWorkOrderIdAsync(
          event.id,
          this.cancelSubject
        );
      if (!response.data) {
        this.isEventScheduleModalOpen = false;
        return;
      }

      const modalRef = this.modalService.open(
        WorkshopEventScheduleModalComponent,
        {
          size: 'md',
          centered: true,
          backdrop: 'static',
        }
      );

      modalRef.componentInstance.eventData = this.mapWorkshopEventScheduleData(
        event,
        response.data
      );
      modalRef.componentInstance.accountIds = this.accountFilter.accountIds ?? [];

      modalRef.closed.subscribe((result) => {
        this.isEventScheduleModalOpen = false;
        if (result) {
          void this.resetMonthCalendarEventsState();
          this.resetTodayCalendarEventsState();
        }
      });

      modalRef.dismissed.subscribe(() => {
        this.isEventScheduleModalOpen = false;
      });
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isEventScheduleModalOpen = false;
        },
      });
    }
  }

  async onEventDrop(data: {
    event: CalendarEvent;
    targetDay: CalendarDay;
  }): Promise<void> {
    if (this.isUpdatingDraggedEvent) {
      return;
    }

    const originalScheduledDate = new Date(data.event.scheduledDate);
    const scheduledDate = this.mergeTargetDateWithEventTime(
      data.targetDay.date,
      originalScheduledDate
    );

    if (scheduledDate < new Date()) {
      this.toastr.warning(this.transloco.translate('workOrder.calendar.pastDateWarning'));
      return;
    }

    this.isUpdatingDraggedEvent = true;
    const previousTop4Events = this.cloneCalendarEvents(this.top4Events);
    const refreshToday =
      isToday(originalScheduledDate) || isToday(scheduledDate);

    try {
      if (data.targetDay.isCurrentMonth) {
        this.applyOptimisticEventDrop(data.event.id, scheduledDate);
      } else {
        this.top4Events = this.top4Events.filter(
          (event) => event.id !== data.event.id
        );
      }

      await this.workOrdersService.rescheduleEventAsync(
        {
          workOrderId: data.event.id,
          assetId: data.event.assetId,
          scheduledDate,
        },
        this.cancelSubject
      );

      await this.refreshCalendarDataAfterEventScheduleUpdate(false, refreshToday, refreshToday);

      this.isUpdatingDraggedEvent = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.top4Events = previousTop4Events;
          this.isUpdatingDraggedEvent = false;
        },
      });
    }
  }

  private async resetMonthCalendarEventsState(): Promise<void> {
    this.expandedDayLoadingKey = null;
    this.clearExpandedDayEvents();
    this.top4Events = [];

    await this.loadCalendarEventsForCurrentView();
  }

  private async resetTodayCalendarEventsState(showLoader: boolean = true): Promise<void> {
    this.todayPage = 1;
    this.todayTotalPages = 0;
    this.todayEvents = [];

    await this.loadTodayCalendarEventsData(showLoader);
  }

  private async refreshCalendarDataAfterEventScheduleUpdate(
    showLoader: boolean = true,
    refreshToday: boolean = true,
    showTodayLoader: boolean = showLoader
  ): Promise<void> {
    this.expandedDayLoadingKey = null;
    this.clearExpandedDayEvents();

    await Promise.all([
      this.loadCalendarEventsForCurrentView(showLoader),
      refreshToday ? this.resetTodayCalendarEventsState(showTodayLoader) : Promise.resolve(),
    ]);
  }

  private async loadCalendarEventsForCurrentView(showLoader: boolean = true): Promise<void> {
    if (
      this.dateFilter.interval === DateInterval.Month ||
      this.dateFilter.interval == null
    ) {
      await this.loadCalendarEventsPreviewData(showLoader);
    } else {
      await this.loadAllCalendarEventsForRange(showLoader);
    }
  }

  private async loadCalendarEventsPreviewData(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingAllEvents = true;
    }

    try {
      const request = this.createCalendarEventsPreviewRequest();
      const response =
        await this.workOrdersService.getWorkshopEventsPreviewAsync(
          request,
          this.cancelSubject
        );

      this.top4Events = (response.data ?? []).map((event) =>
        this.mapCalendarEvent(event)
      );
      if (showLoader) {
        this.isLoadingAllEvents = false;
      }
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (showLoader) {
            this.isLoadingAllEvents = false;
          }
        },
      });
    }
  }

  private async loadCalendarEventsByDateData(date: Date): Promise<void> {
    const dayKey = this.getDayKey(date);
    this.expandedDayLoadingKey = dayKey;
    this.expandedDayKey = dayKey;
    this.expandedDayEvents = [];

    try {
      const request = this.createCalendarEventsByDateRequest(date);
      const response =
        await this.workOrdersService.getWorkshopEventsByDateAsync(
          request,
          this.cancelSubject
        );

      this.expandedDayEvents = (response.data ?? []).map((event) =>
        this.mapCalendarEvent(event)
      );
      this.expandedDayLoadingKey = null;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.expandedDayLoadingKey = null;
        },
      });
    }
  }

  private async loadAllCalendarEventsForRange(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingAllEvents = true;
    }

    try {
      const { timeFrom, timeTo } = this.getCalendarDateRange();
      const request: WorkshopEventsByDateRequest = {
        accountIds: this.accountFilter.accountIds,
        date: timeFrom,
        endOfDay: timeTo,
        serviceTypes: this.getSelectedServiceTypes(),
        showMissedEvents: this.shouldShowMissedEvents(),
        searchTerm: this.searchTerm,
      };

      const response =
        await this.workOrdersService.getWorkshopEventsByDateAsync(
          request,
          this.cancelSubject
        );

      this.top4Events = (response.data ?? []).map((event) =>
        this.mapCalendarEvent(event)
      );
      if (showLoader) {
        this.isLoadingAllEvents = false;
      }
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (showLoader) {
            this.isLoadingAllEvents = false;
          }
        },
      });
    }
  }

  private async loadTodayCalendarEventsData(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingTodayEvents = true;
    }

    try {
      const response = await this.workOrdersService.getTodayWorkshopEventsAsync(
        this.createTodayWorkshopEventsRequest(),
        this.cancelSubject
      );
      const todayEvents = response.data?.results ?? [];
      const newTodayEvents = todayEvents.map((event) =>
        this.mapTodayEvent(event)
      );

      this.todayEvents =
        this.todayPage === 1
          ? newTodayEvents
          : [...this.todayEvents, ...newTodayEvents];
      this.todayTotalPages = response.data?.totalPages ?? 0;

      if (showLoader) {
        this.isLoadingTodayEvents = false;
      }
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          if (showLoader) {
            this.isLoadingTodayEvents = false;
          }
        },
      });
    }
  }

  private createCalendarEventsPreviewRequest(): WorkshopEventsPreviewRequest {
    const { timeFrom, timeTo } = this.getCalendarDateRange();

    return {
      accountIds: this.accountFilter.accountIds,
      timeFrom,
      timeTo,
      timezoneOffset: new Date().getTimezoneOffset(),
      serviceTypes: this.getSelectedServiceTypes(),
      showMissedEvents: this.shouldShowMissedEvents(),
      searchTerm: this.searchTerm,
    };
  }

  private createCalendarEventsByDateRequest(
    date: Date
  ): WorkshopEventsByDateRequest {
    return {
      accountIds: this.accountFilter.accountIds,
      date,
      endOfDay: endOfDay(date),
      serviceTypes: this.getSelectedServiceTypes(),
      showMissedEvents: this.shouldShowMissedEvents(),
      searchTerm: this.searchTerm,
    };
  }

  private createTodayWorkshopEventsRequest(): TodayWorkshopEventsPaginationRequest {
    const today = new Date();

    return {
      accountIds: this.accountFilter.accountIds ?? [],
      timeFrom: startOfDay(today),
      timeTo: endOfDay(today),
      page: this.todayPage,
      pageSize: 20,
    };
  }

  private mapCalendarEvent(event: WorkshopEventResponse): CalendarEvent {
    const scheduledDate = new Date(event.scheduledDate);

    return {
      id: event.id,
      assetId: event.assetId,
      assetName: event.assetName,
      type: this.normalizeServiceType(event.type),
      startTime: this.formatDateTimeToTime(scheduledDate),
      scheduledDate,
      isMissedEvent: event.isMissedEvent,
    };
  }

  private mapTodayEvent(event: TodayWorkshopEventResponse): CalendarTodayEvent {
    const startTime = new Date(event.startTime);
    const endTime = event.endTime ? new Date(event.endTime) : null;

    return {
      id: event.id,
      assetId: event.assetId,
      assetName: event.assetName,
      type: this.normalizeServiceType(event.type),
      startTime: this.formatDateTimeToTime(startTime),
      isMissedEvent: event.isMissedEvent,
      endTime: endTime ? this.formatDateTimeToTime(endTime) : null,
      workshop: event.workshopName?.trim() ? event.workshopName : '-',
    };
  }

  private mapWorkshopEventScheduleData(
    event: CalendarEvent,
    eventSchedule: WorkshopEventScheduleResponse
  ): WorkshopEventScheduleData {
    return {
      id: event.id,
      workOrderId: event.id,
      assetName: eventSchedule.assetName ?? event.assetName,
      make: eventSchedule.make ?? '',
      model: eventSchedule.model ?? '',
      odometer: this.formatOdometerReading(eventSchedule.odometerReading),
      type: eventSchedule.serviceType
        ? this.mapServiceTypeIdToCalendarType(eventSchedule.serviceType)
        : event.type,
      serviceCentre: eventSchedule.serviceCentre ?? '',
      scheduledDate: eventSchedule.scheduledDate ?? event.scheduledDate,
      serviceItems:
        eventSchedule.serviceItems ?? eventSchedule.partsRequired ?? [],
      startTime: event.startTime,
      endTime: event.endTime,
    };
  }

  private getCalendarDateRange(): { timeFrom: Date; timeTo: Date } {
    return {
      timeFrom: this.dateFilter.startDate ?? startOfMonth(this.currentDate),
      timeTo: this.dateFilter.endDate ?? endOfMonth(this.currentDate),
    };
  }

  private normalizeServiceType(
    type: string
  ): 'major' | 'intermediate' | 'minor' {
    switch (type.trim().toLowerCase()) {
      case 'major':
        return 'major';
      case 'intermediate':
        return 'intermediate';
      case 'minor':
      default:
        return 'minor';
    }
  }

  private mapServiceTypeIdToCalendarType(
    serviceType: number
  ): 'major' | 'intermediate' | 'minor' {
    switch (serviceType) {
      case 3:
        return 'major';
      case 2:
        return 'intermediate';
      case 1:
      default:
        return 'minor';
    }
  }

  private getSelectedServiceTypes(): number[] {
    return this.calendarConfig.filterOptions
      .filter((option) => option.checked)
      .map((option) => {
        switch (option.key) {
          case 'major':
            return 3;
          case 'intermediate':
            return 2;
          case 'minor':
            return 1;
          default:
            return 0;
        }
      })
      .filter((id) => id > 0);
  }

  private shouldShowMissedEvents(): boolean {
    return this.calendarConfig.missedEventFilter?.checked ?? false;
  }

  private clearExpandedDayEvents(): void {
    this.expandedDayKey = null;
    this.expandedDayEvents = [];
  }

  private mergeTargetDateWithEventTime(
    targetDate: Date,
    sourceDateTime: Date
  ): Date {
    return new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate(),
      sourceDateTime.getHours(),
      sourceDateTime.getMinutes(),
      sourceDateTime.getSeconds(),
      sourceDateTime.getMilliseconds()
    );
  }

  private applyOptimisticEventDrop(eventId: number, scheduledDate: Date): void {
    this.top4Events = this.top4Events.map((event) => {
      if (event.id !== eventId) {
        return event;
      }

      return {
        ...event,
        scheduledDate: new Date(scheduledDate),
        startTime: this.formatDateTimeToTime(scheduledDate),
      };
    });
  }

  private getDayKey(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private cloneCalendarEvents(events: CalendarEvent[]): CalendarEvent[] {
    return events.map((event) => ({
      ...event,
      scheduledDate: new Date(event.scheduledDate),
    }));
  }

  private formatDateTimeToTime(value: Date): string {
    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private formatOdometerReading(value: number | null | undefined): string {
    return value == null ? '' : `${value.toLocaleString()} km`;
  }
}